import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faInfoCircle } from '@/fontawesome'
import { EXTERNAL_APIS, TIME_INTERVALS, FEAR_GREED_ZONES } from '@/config'

type IndexZone = 'EXTREME_FEAR' | 'FEAR' | 'NEUTRAL' | 'GREED' | 'EXTREME_GREED'

const getZone = (value: number): IndexZone => {
  if (value < FEAR_GREED_ZONES.EXTREME_FEAR_MAX) return 'EXTREME_FEAR'
  if (value < FEAR_GREED_ZONES.FEAR_MAX) return 'FEAR'
  if (value < FEAR_GREED_ZONES.NEUTRAL_MAX) return 'NEUTRAL'
  if (value < FEAR_GREED_ZONES.GREED_MAX) return 'GREED'
  return 'EXTREME_GREED'
}

const getZoneLabel = (zone: IndexZone): string => {
  switch (zone) {
    case 'EXTREME_FEAR':
      return 'Extreme Angst'
    case 'FEAR':
      return 'Angst'
    case 'NEUTRAL':
      return 'Neutral'
    case 'GREED':
      return 'Gier'
    case 'EXTREME_GREED':
      return 'Extreme Gier'
  }
}

const getZoneColor = (zone: IndexZone): string => {
  switch (zone) {
    case 'EXTREME_FEAR':
      return 'text-red-600 dark:text-red-400'
    case 'FEAR':
      return 'text-orange-600 dark:text-orange-400'
    case 'NEUTRAL':
      return 'text-gray-600 dark:text-gray-400'
    case 'GREED':
      return 'text-green-600 dark:text-green-400'
    case 'EXTREME_GREED':
      return 'text-green-700 dark:text-green-300'
  }
}

const getZoneDescription = (zone: IndexZone): string => {
  switch (zone) {
    case 'EXTREME_FEAR':
      return 'Der Markt zeigt extreme Angst. Investoren verkaufen in Panik. Historisch gesehen können dies gute Kaufgelegenheiten sein, da Aktien oft unterbewertet sind.'
    case 'FEAR':
      return 'Angst dominiert den Markt. Investoren sind vorsichtig und verkaufen. Dies kann Chancen für langfristige Investoren bieten.'
    case 'NEUTRAL':
      return 'Der Markt ist ausgeglichen. Weder Angst noch Gier dominieren. Investoren agieren rational und ausgewogen.'
    case 'GREED':
      return 'Gier zeigt sich am Markt. Investoren kaufen aggressiv. Vorsicht ist geboten, da Aktien möglicherweise überbewertet sind.'
    case 'EXTREME_GREED':
      return 'Extreme Gier treibt den Markt. Investoren kaufen ohne Rücksicht. Hohes Risiko einer Korrektur. Gewinnmitnahmen könnten sinnvoll sein.'
  }
}

export default function FearGreedIndexDetail() {
  const [value, setValue] = useState<number | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFearGreedIndex = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await fetch(EXTERNAL_APIS.FEAR_GREED_INDEX)

        if (!response.ok) {
          console.error(`Fear & Greed API responded with status ${response.status}`)
          setError('Fehler beim Laden des Index')
          setValue(50)
          setLastUpdated(new Date())
          return
        }

        const data = await response.json()

        let score: number | undefined
        let timestamp: string | undefined

        if (data.fear_and_greed) {
          score = data.fear_and_greed.score || data.fear_and_greed.value
          timestamp = data.fear_and_greed.timestamp || data.fear_and_greed.last_update
        } else if (data.score !== undefined || data.value !== undefined) {
          score = data.score || data.value
          timestamp = data.timestamp || data.last_update
        }

        if (score === undefined || isNaN(score)) {
          console.error('Invalid Fear & Greed response format')
          setError('Fehler beim Laden des Index')
          setValue(50)
          setLastUpdated(new Date())
          return
        }

        setValue(Math.round(score))
        setLastUpdated(timestamp ? new Date(timestamp) : new Date())
      } catch (err) {
        console.error('Error fetching Fear & Greed Index:', err)
        setError('Fehler beim Laden des Index')
        setValue(50)
        setLastUpdated(new Date())
      } finally {
        setLoading(false)
      }
    }

    fetchFearGreedIndex()

    const interval = setInterval(fetchFearGreedIndex, TIME_INTERVALS.FEAR_GREED_REFRESH)

    return () => clearInterval(interval)
  }, [])

  if (loading && value === null) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-md border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center min-h-[400px]">
        <FontAwesomeIcon icon={faSpinner} className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
        <p className="text-lg text-gray-500 dark:text-gray-400">Lade Fear & Greed Index...</p>
      </div>
    )
  }

  const currentValue = value ?? 50
  const zone = getZone(currentValue)
  const zoneLabel = getZoneLabel(zone)
  const zoneColor = getZoneColor(zone)
  const zoneDescription = getZoneDescription(zone)

  const needleAngle = (currentValue / 100) * 180

  const radius = 140
  const strokeWidth = 30
  const centerX = 160
  const centerY = 160

  const createArc = (startAngle: number, endAngle: number): string => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle)
    const end = polarToCartesian(centerX, centerY, radius, startAngle)
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`
  }

  function polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    }
  }

  const needleLength = radius - 15
  const needleEnd = polarToCartesian(centerX, centerY, needleLength, needleAngle - 90)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 sm:p-8 shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faInfoCircle} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Fear & Greed Index
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            CNN Money Markt-Stimmungsbarometer
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* SVG Gauge */}
        <div className="flex flex-col items-center justify-center">
          <svg viewBox="0 0 320 220" className="w-full max-w-md">
            {/* Background arcs */}
            <path d={createArc(-90, -45)} fill="none" stroke="#ef4444" strokeWidth={strokeWidth} className="dark:stroke-red-400" />
            <path d={createArc(-45, -18)} fill="none" stroke="#f97316" strokeWidth={strokeWidth} className="dark:stroke-orange-400" />
            <path d={createArc(-18, 18)} fill="none" stroke="#9ca3af" strokeWidth={strokeWidth} className="dark:stroke-gray-500" />
            <path d={createArc(18, 45)} fill="none" stroke="#22c55e" strokeWidth={strokeWidth} className="dark:stroke-green-400" />
            <path d={createArc(45, 90)} fill="none" stroke="#16a34a" strokeWidth={strokeWidth} className="dark:stroke-green-500" />

            {/* Needle */}
            <line x1={centerX} y1={centerY} x2={needleEnd.x} y2={needleEnd.y} stroke="#1f2937" strokeWidth="4" strokeLinecap="round" className="dark:stroke-white" />

            {/* Center circle */}
            <circle cx={centerX} cy={centerY} r="6" fill="#1f2937" className="dark:fill-white" />

            {/* Zone labels */}
            <text x="30" y="190" textAnchor="start" className="text-xs fill-gray-500 dark:fill-gray-400">0</text>
            <text x={centerX} y="190" textAnchor="middle" className="text-xs fill-gray-500 dark:fill-gray-400">50</text>
            <text x="290" y="190" textAnchor="end" className="text-xs fill-gray-500 dark:fill-gray-400">100</text>

            {/* Background for value */}
            <rect x={centerX - 40} y={centerY + 10} width="80" height="50" fill="white" className="dark:fill-gray-800" />

            {/* Value display */}
            <text x={centerX} y={centerY + 45} textAnchor="middle" className="text-5xl font-bold fill-gray-900 dark:fill-white">{currentValue}</text>
          </svg>

          {/* Current zone */}
          <div className="mt-4 text-center">
            <p className={`text-2xl font-bold ${zoneColor} mb-2`}>
              {zoneLabel}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Letzte Aktualisierung: {lastUpdated?.toLocaleString('de-DE')}
            </p>
            {error !== null && (
              <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Aktuelle Marktstimmung
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {zoneDescription}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Zonen-Übersicht
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-red-500 dark:bg-red-400 rounded"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">0-24: Extreme Angst</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-orange-500 dark:bg-orange-400 rounded"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">25-44: Angst</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-gray-500 dark:bg-gray-400 rounded"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">45-54: Neutral</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-green-500 dark:bg-green-400 rounded"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">55-74: Gier</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-green-700 dark:bg-green-500 rounded"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">75-100: Extreme Gier</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Hinweis:</strong> Der Fear & Greed Index basiert auf verschiedenen Marktindikatoren und misst die allgemeine Stimmung am Aktienmarkt. Er dient als Entscheidungshilfe, sollte aber nicht als alleiniger Faktor für Investitionsentscheidungen verwendet werden.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
